import type { Guide } from './types.js';

/**
 * The guide shown to USPS users. It describes only what a USPS account can see and
 * do: no VGS cost build-up, no markup mechanics, no internal notes. Anything a USPS
 * reader could not verify from their own screen does not belong here.
 */
export const USPS_GUIDE: Guide = {
  title: 'USPS user guide',
  subtitle:
    'How to request repairs, follow them, authorize proposals, and pull the reports the agreement requires.',
  audience:
    'Written for USPS requestors, Contracting Officer’s Representatives, and Contracting Officers.',
  sections: [
    {
      id: 'overview',
      title: '1. What this platform is',
      blocks: [
        {
          kind: 'paragraph',
          text: 'VGS Work Order Management is the portal for Reimbursable Variable Service and Repair work under your task order. You use it to request a repair, watch what happens to that request, authorize the price before any work is performed, see the completion record afterwards, and export reporting at any cadence the Contracting Officer asks for.',
        },
        {
          kind: 'paragraph',
          text: 'Every request becomes a numbered work order that carries its own history. Nothing in that history can be edited away afterwards: status changes, approvals, comments, and documents are appended, not overwritten. When you are asked months later why a repair was authorized at a given price, the answer is in the work order.',
        },
        {
          kind: 'bullets',
          items: [
            'Work is only performed after USPS authorizes it. VGS cannot authorize its own proposal — no VGS account has approval authority in the system at all.',
            'The price you authorize is the price on the proposal. If the scope changes, VGS must submit a revised proposal and it returns to you for a fresh decision.',
            'Approval authority follows the dollar value, so a proposal is routed to the right USPS role automatically rather than by anyone remembering the threshold.',
          ],
        },
      ],
    },
    {
      id: 'accounts',
      title: '2. Getting your account',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Accounts are invitation-only; there is no public sign-up. VGS administration issues an invitation to your work email, scoped to the facilities you are entitled to see.',
        },
        {
          kind: 'steps',
          items: [
            'Open the invitation email and follow the link. Invitation links are single-use and expire, so if it has been sitting in your inbox for a while, ask for a fresh one.',
            'Confirm your name, add your title and phone number, and set a password. Your role and any dollar authority are set by your administrator and are not something you choose — that is deliberate, because self-asserted authority would undermine the approval record.',
            'Sign in at the platform address. If you forget your password, use “Forgot password” on the sign-in page; the reset link is single-use and short-lived.',
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'You will only ever see your own facilities',
          text: 'Access is scoped by facility and task order. A COR at one facility does not see another facility’s work orders, its documents, or its numbers — including in reports and exports.',
        },
      ],
    },
    {
      id: 'roles',
      title: '3. USPS roles and what each one can do',
      blocks: [
        {
          kind: 'table',
          headers: ['Role', 'Can do', 'Cannot do'],
          rows: [
            [
              'USPS User (requestor)',
              'Report repairs to VGS, track the resulting work orders, comment, attach photos and documents, view proposals, run reports for their facilities.',
              'Authorize a proposal.',
            ],
            [
              'Contracting Officer’s Representative (COR)',
              'Everything a USPS User can do, plus approve, reject, or request revision of a proposal within the COR dollar limit; refer a proposal upward to the Contracting Officer.',
              'Authorize above the COR limit.',
            ],
            [
              'Contracting Officer (CO)',
              'Everything a COR can do, at any value permitted by the agreement; rule on requests to proceed with fewer than three quotes; require competition on a specific work order; exercise discretion where the Facilities Response Line declines the work.',
              '—',
            ],
          ],
        },
        {
          kind: 'paragraph',
          text: 'A facility can have more than one COR and more than one CO. Any of them may act; the platform records which individual made each decision, when, and under what authority.',
        },
      ],
    },
    {
      id: 'raise',
      title: '4. Requesting a repair',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Only VGS enters work orders. To request a repair, report the problem to VGS by telephone or email and VGS records it as a work order, which you can then see and follow in the portal.',
        },
        {
          kind: 'steps',
          items: [
            'Tell VGS the facility and the repair category (HVAC, Electrical, Plumbing, Roofing, Fire/Life Safety, and so on), and how urgent it is: Emergency, Urgent, Routine, or Scheduled.',
            'Describe where the problem is — building or area, specific location, and the equipment or asset tag if you know it. The more precise this is, the less likely a site visit is wasted.',
            'Describe the problem itself, and mention any access constraints, permitted working hours, or escort requirements.',
            'Name the point of contact for the site: name, email, and phone. This is who VGS calls to arrange access, and it appears on the reporting as local facility contact information.',
            'Give a requested completion date if one matters.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'VGS enters the request and a work order number is issued — for example RE-TUL-000012, where the middle segment is the facility. Use that number in any correspondence.',
        },
        {
          kind: 'paragraph',
          text: 'Once the work order exists you can add photographs of the fault, a sketch, or any document that helps, from Documents & comments on the work order.',
        },
      ],
    },
    {
      id: 'lifecycle',
      title: '5. What happens next: the statuses you will see',
      blocks: [
        {
          kind: 'table',
          headers: ['Status', 'What it means', 'Who is holding it'],
          rows: [
            ['Submitted', 'Your request is in and VGS has been notified.', 'VGS'],
            ['Under VGS Review', 'VGS has picked it up and is assessing what is needed.', 'VGS'],
            [
              'Estimate in Progress',
              'VGS is investigating on site and pricing the repair; quotes are being sought where competition applies.',
              'VGS',
            ],
            [
              'Awaiting USPS Approval',
              'A priced proposal has been submitted to USPS. Nothing further happens until a COR or CO decides.',
              'USPS',
            ],
            ['Approved', 'The proposal is authorized. VGS may schedule the work.', 'VGS'],
            [
              'Repair Scheduled',
              'A date and time window are set; you can see them on the work order.',
              'VGS',
            ],
            ['Work in Progress', 'The repair is under way.', 'VGS'],
            [
              'Completed',
              'The work is finished and the completion record — what was performed, when, with photographs — is posted.',
              'VGS',
            ],
            [
              'Closed',
              'The work order is finalized. Its record stays available and reportable.',
              '—',
            ],
            [
              'Revision Requested',
              'You asked for changes to the proposal; it goes back to VGS to re-price or re-scope.',
              'VGS',
            ],
            ['Rejected', 'The proposal was declined. No work is performed.', '—'],
            [
              'On Hold',
              'Progress is paused — waiting on parts, access, or a decision. The reason is recorded, and releasing the hold returns the work order to exactly the stage it interrupted.',
              'VGS',
            ],
            ['Cancelled', 'The request was withdrawn. The reason is recorded.', '—'],
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'The order is enforced, not suggested',
          text: 'The platform only permits the moves shown above, and the rule is enforced twice — once in the application and once in the database. A repair cannot be marked complete without a completion record, and it cannot skip your authorization.',
        },
      ],
    },
    {
      id: 'proposal',
      title: '6. Reviewing a proposal',
      blocks: [
        {
          kind: 'paragraph',
          text: 'When VGS submits a proposal, the work order moves to Awaiting USPS Approval and the approvers for that facility are emailed and notified in the app. Open the work order, or go to “Awaiting my approval”, which lists everything waiting on you.',
        },
        {
          kind: 'paragraph',
          text: 'A proposal shows the scope of work, a summary of what the investigation found, the priced line items, and the total price to USPS. Supporting documents — including subcontractor quotes where a subcontractor is being used — are attached to the work order for you to open.',
        },
        {
          kind: 'bullets',
          items: [
            'Approve — authorizes the total shown.',
            'Request revision — sends it back to VGS with your comment. VGS re-prices and resubmits as a new version; the earlier version stays on the record.',
            'Reject — declines the work. A reason is required.',
            'Refer to the Contracting Officer — where the value or the circumstances call for CO authorization rather than yours.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Every version of every proposal is retained. When you look at an approved work order later, you can see what was originally proposed, what you asked to change, and what you finally authorized.',
        },
      ],
    },
    {
      id: 'authority',
      title: '7. Approval authority and the thresholds',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Authority is tested against the total price to USPS — the figure you are being asked to obligate and the figure that appears on the invoice.',
        },
        {
          kind: 'table',
          headers: ['Total price to USPS', 'Who may authorize', 'Additional requirement'],
          rows: [
            ['Up to $5,000', 'COR or CO', 'None beyond the normal review.'],
            [
              'Over $5,000',
              'COR, then Contracting Officer',
              'The COR reviews and endorses first; the CO gives the final approval. VGS is told to proceed only after the CO approves.',
            ],
            [
              'Over $10,000',
              'Contracting Officer',
              'The USPS Facilities Response Line must first determine whether the work qualifies as a project. The ticket number, the date of the call, and the outcome are recorded on the work order.',
            ],
          ],
        },
        {
          kind: 'paragraph',
          text: 'These figures are configuration, not code: if a contract modification changes them, an administrator changes the numbers and future proposals route accordingly. The authority that applied on the day of each approval is stored with that approval, so historical records continue to read correctly.',
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'A revision can change who must approve it',
          text: 'If a $4,800 proposal comes back revised at $5,400, it no longer rides the COR’s earlier authority — the platform re-routes it to the Contracting Officer. The system refuses an approval made without sufficient authority rather than accepting it and flagging it later.',
        },
        {
          kind: 'paragraph',
          text: 'Where the Facilities Response Line declines the work, the Contracting Officer may still authorize it if that serves the best interests of the Postal Service. That discretion is recorded as its own act, by name and timestamp, distinct from the approval itself.',
        },
      ],
    },
    {
      id: 'competition',
      title: '8. Competition and quotes',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Where competition applies, VGS seeks at least three quotes. Fewer than three is possible only as an exception: VGS records how many quotes were obtained and why more were not available, and the Contracting Officer approves or rejects that request in the platform. The decision, the justification, and the decider are all part of the work order record.',
        },
        {
          kind: 'paragraph',
          text: 'A Contracting Officer can also require competition on a specific work order that would not otherwise call for it, using the Competition panel on that work order.',
        },
      ],
    },
    {
      id: 'during',
      title: '9. Following the work',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Timeline — every status change, who made it, when, and any reason given.',
            'Comments — a running conversation with VGS on the work order. Anything you post is visible to VGS.',
            'Documents and photos — investigation photographs, subcontractor quotes, completion photographs, and anything you upload yourself. Files up to 25 MB, in PDF, JPG, PNG, HEIC, WEBP, DOC, DOCX, XLS, XLSX, or CSV.',
            'Schedule — the scheduled date and time window once the repair is booked.',
            'Completion — what was performed, when it started and finished, follow-up recommendations, and any exceptions noted.',
            'Notifications — the bell in the header, plus email for the events that need you: a proposal awaiting your approval, and the decision once it is made.',
          ],
        },
      ],
    },
    {
      id: 'reports',
      title: '10. Reports and data exchange',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Reports page carries two reports, both filterable by date range and both scoped to what your account is entitled to see.',
        },
        {
          kind: 'definitions',
          items: [
            {
              term: 'Work order report',
              description:
                'One row per work order, carrying every field the agreement requires: work order number; commodity or service provided; service location name, address, and Property ID; Postal Service Area/Logistics; Postal Service District/Division; work description; local facility contact information; date the request was received; date approved, being the notice to proceed; project cost; performance start date; project or service status; invoice number where applicable; and notes or comments including sourcing delays.',
            },
            {
              term: 'Approvals report',
              description:
                'One row per approval decision — who decided, when, on what value, and under which authority — which is what supports monthly invoice backup.',
            },
          ],
        },
        {
          kind: 'paragraph',
          text: 'Use the column picker to choose exactly which fields appear; the CSV export contains precisely the columns you selected, in the same order, so a recurring monthly or quarterly extract can be set up once and repeated. Project Cost is the USPS-authorized total.',
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'Any cadence, on demand',
          text: 'Monthly, quarterly, annual, or any other period the Contracting Officer asks for is a date-range filter on the same live data — there is no reporting cycle to wait for, and the portal itself is available to the Contracting Officer and the FSCMC in real time.',
        },
      ],
    },
    {
      id: 'help',
      title: '11. Getting help',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'A repair that needs immediate attention: raise the work order at Emergency priority and call your VGS facility contact — the platform notifies, but it does not replace a phone call for a genuine emergency.',
            'Access problems, or a facility you should be able to see but cannot: contact VGS administration; facility access is granted per account.',
            'A record that looks wrong: comment on the work order rather than emailing separately, so the correction stays attached to the work order it concerns.',
          ],
        },
      ],
    },
  ],
};
